<template>
  <v-dialog persistent max-width="800px" v-model="dialog" fullscreen transition="dialog-bottom-transition">
    <div class="course-edit-wrapper">
      <v-app-bar app color="white" flat class="app-bar-border">
        <v-btn icon @click="closeDialog()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title v-if="courseTimelineEdit" class="font-weight-bold">
          {{ courseTimelineEdit.course.name }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="showPushPopupMethod">
          <v-icon left>mdi-check</v-icon>
          Speichern
        </v-btn>
      </v-app-bar>

      <v-container v-if="loading || error || !courseTimelineEdit" class="d-flex justify-center align-center" style="height: 100vh">
        <div v-if="loading" class="text-center">
          <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
          <p class="mt-4 grey--text">Kursplanung wird geladen...</p>
        </div>
        <v-alert v-if="error" type="error" prominent> Kursplanung konnte nicht gefunden oder geladen werden. </v-alert>
      </v-container>

      <v-container v-else class="form-container">
        <v-row no-gutters>
          <v-col cols="12">
            <v-alert type="info" text prominent>
              Änderungen hier beeinflussen alle zukünftigen Kurse dieser Planung. Manuell angepasste einzelne Kurse bleiben unberührt.
            </v-alert>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Grundlagen</v-card-title>
              <v-card-subtitle>Passe das Kursformat und die Basis-Infos an.</v-card-subtitle>
              <v-card-text>
                <course-search
                  :value="courseTimelineEdit.course"
                  label="Kursformat"
                  rights="gf_c_plan"
                  @update="updateCourse"
                  outlined></course-search>
                <v-select
                  v-model="courseTimelineEdit.label"
                  :items="labels"
                  item-text="text"
                  item-value="_id"
                  label="Label (optional)"
                  outlined
                  class="mt-4"
                  clearable></v-select>
                <v-text-field
                  v-model="courseTimelineEdit.additionalInfo"
                  outlined
                  label="Zusatzinfo (optional)"
                  placeholder="z.B. Mindest-Teilnehmerzahl"></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Zeitplanung</v-card-title>
              <v-card-subtitle>Lege fest, wann und wie oft der Kurs stattfindet.</v-card-subtitle>
              <v-card-text>
                <v-btn-toggle v-model="courseTimelineEdit.repeating" mandatory dense class="mb-6">
                  <v-btn :value="true">Wöchentlich</v-btn>
                  <v-btn :value="false">Begrenzt</v-btn>
                </v-btn-toggle>

                <v-select
                  v-if="courseTimelineEdit.repeating"
                  v-model="courseTimelineEdit.day"
                  :items="days"
                  item-text="text"
                  item-value="value"
                  outlined
                  label="Wochentag"></v-select>

                <div v-else>
                  <p class="body-2 grey--text text--darken-1">Wähle den Zeitraum, in dem der Kurs stattfinden soll.</p>
                  <v-select v-model="courseTimelineEdit.day" :items="days" item-text="text" item-value="value" outlined label="Wochentag"></v-select>
                  <v-menu v-model="dateMenuVisible" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="visibleAfterText"
                        label="Anzeigen ab"
                        prepend-inner-icon="mdi-calendar-start"
                        readonly
                        outlined
                        v-bind="attrs"
                        v-on="on"></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="courseTimelineEdit.visibleAfter"
                      @input="dateMenuVisible = false"
                      :min="minDate"
                      :allowed-dates="allowedDates(selectedDay)"
                      locale="de-CH"></v-date-picker>
                  </v-menu>

                  <v-menu v-model="dateMenuHide" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="hideAfterText"
                        label="Anzeigen bis"
                        prepend-inner-icon="mdi-calendar-end"
                        readonly
                        outlined
                        v-bind="attrs"
                        v-on="on"></v-text-field>
                    </template>
                    <v-date-picker
                      :allowed-dates="allowedDates(selectedDay)"
                      v-model="courseTimelineEdit.hideAfter"
                      @input="dateMenuHide = false"
                      :min="courseTimelineEdit.visibleAfter"
                      locale="de-CH"></v-date-picker>
                  </v-menu>
                </div>

                <v-row>
                  <v-col cols="6"><v-text-field v-model="time" type="time" outlined label="Uhrzeit"></v-text-field></v-col>
                  <v-col cols="6"
                    ><v-text-field v-model="courseTimelineEdit.duration" type="number" outlined label="Dauer (Min)"></v-text-field
                  ></v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Kursleiter & Ort</v-card-title>
              <v-card-text>
                <div v-for="(instructor, index) in courseTimelineEdit.instructors" :key="index" class="d-flex align-center">
                  <user-search
                    instructor
                    :value="instructor"
                    :key="index + instructor + '-inst'"
                    label="Instruktor"
                    rights="gf_c_plan"
                    @update="(newVal) => updateInstructor(newVal, instructor)"
                    outlined
                    class="flex-grow-1"></user-search>
                  <v-btn icon color="grey" @click="removeInstructor(instructor)" class="ml-2"><v-icon>mdi-delete</v-icon></v-btn>
                </div>
                <v-btn text color="primary" @click="addInstructor" class="mt-2"><v-icon left>mdi-plus</v-icon> Instruktor hinzufügen</v-btn>
                <v-select
                  v-model="courseTimelineEdit.room"
                  :items="rooms"
                  item-text="name"
                  item-value="_id"
                  outlined
                  label="Raum"
                  class="mt-6"></v-select>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Teilnahme & Darstellung</v-card-title>
              <v-card-text>
                <v-switch v-model="courseTimelineEdit.reqSignup" label="Anmeldung erforderlich"></v-switch>
                <v-text-field
                  v-if="courseTimelineEdit.reqSignup"
                  v-model="courseTimelineEdit.amount"
                  type="number"
                  outlined
                  label="Anzahl Plätze"></v-text-field>
                <v-divider class="my-4"></v-divider>
                <v-subheader>Kurs-Icon</v-subheader>
                <v-chip-group column v-model="courseTimelineEdit.icon" active-class="primary">
                  <v-chip v-for="ic in icons" large :key="ic" :value="ic" filter>
                    <v-avatar class="iconAvatar" center>
                      <lottie-player mode="normal" :src="'/api/gf/icons/' + ic"></lottie-player>
                    </v-avatar>
                  </v-chip>
                </v-chip-group>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined style="border-color: #d32f2f">
              <v-card-title class="red--text text--darken-2">Gefahrenzone</v-card-title>
              <v-card-text>
                <p class="body-1">
                  Das Löschen einer Kursplanung entfernt **alle** zukünftigen, noch nicht stattgefundenen Kurse, die auf dieser Planung basieren.
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
                <v-btn block large dark color="red darken-2" @click="deleteTimeline(courseTimelineEdit)">
                  <v-icon left>mdi-delete-forever</v-icon>
                  Kursplanung löschen
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-dialog v-model="showPushPopUp" persistent max-width="400px">
        <v-card>
          <v-card-title class="text-h5">Teilnehmer benachrichtigen?</v-card-title>
          <v-card-text> Möchtest du die bereits angemeldeten Teilnehmer per Push-Nachricht über die Änderungen informieren? </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="initSaveTimeline(false)">Nein, nicht senden</v-btn>
            <v-btn color="primary" @click="initSaveTimeline(true)">Ja, benachrichtigen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-dialog>
</template>

<script>
import api from "../../api"
import { mapActions } from "vuex"
import userSearch from "../../components/userSearch.vue"
import courseSearch from "../../components/courseSearch.vue"

export default {
  name: "courseEditTimeline",
  components: { userSearch, courseSearch },
  data() {
    return {
      error: false,
      loading: true,
      showPushPopUp: false,
      time: "00:00",
      dialog: true,
      courseTimelineEdit: null,
      labels: [{ _id: null, text: "Kein Label" }],
      rooms: [],
      visibleAfter: false,
      hideAfter: false,
      dateMenuVisible: false,
      dateMenuHide: false,
      minDate: null,
      icons: [],
      days: [
        { value: 1, text: "Montag" },
        { value: 2, text: "Dienstag" },
        { value: 3, text: "Mittwoch" },
        { value: 4, text: "Donnerstag" },
        { value: 5, text: "Freitag" },
        { value: 6, text: "Samstag" },
        { value: 7, text: "Sonntag" },
      ],
    }
  },
  async created() {
    this.minDate = this.$moment().format("YYYY-MM-DD")
    const [allLabels, courseTimeline, rooms, icons] = await Promise.all([
      api.fetchGFLabels(),
      api.fetchCourseTimeline(this.$route.params.id).catch(() => {
        this.error = true
      }),
      api.getRooms(),
      api.fetchAllGFIcons(),
    ]).catch(() => {
      this.error = true
    })

    if (this.error || !courseTimeline) {
      this.loading = false
      return
    }

    this.labels = this.labels.concat(allLabels)
    this.rooms = rooms.room
    this.icons = icons.icons
    this.courseTimelineEdit = courseTimeline.timeline

    // Zeit korrekt formatieren
    let hours = String(this.courseTimelineEdit.hours).padStart(2, "0")
    let minutes = String(this.courseTimelineEdit.minutes).padStart(2, "0")
    this.time = `${hours}:${minutes}`

    // Daten für zeitlich begrenzte Kurse vorbereiten
    if (this.courseTimelineEdit.visibleAfter) {
      this.visibleAfter = true
      this.courseTimelineEdit.visibleAfter = this.$moment.unix(this.courseTimelineEdit.visibleAfter).format("YYYY-MM-DD")
    }
    if (this.courseTimelineEdit.hideAfter) {
      this.hideAfter = true
      this.courseTimelineEdit.hideAfter = this.$moment.unix(this.courseTimelineEdit.hideAfter).format("YYYY-MM-DD")
    }

    this.loading = false
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationBarText"]),
    allowedDates(a) {
      return (val) => !a.includes(new Date(val).getDay())
    },
    showPushPopupMethod() {
      if (this.courseTimelineEdit.instructors.includes(null)) {
        this.$toast.error("Bitte wähle für alle Felder einen Instruktor aus oder entferne die leeren Felder.")
        return // <-- Hier wird abgebrochen
      }
      this.showPushPopUp = true
    },
    updateCourse(newCourse) {
      this.courseTimelineEdit.course = newCourse
    },

    async deleteTimeline(timeline) {
      if (
        confirm(`Möchtest du die Kursplanung für "${timeline.course.name}" definitiv löschen? Alle zukünftigen Kurse dieser Planung werden entfernt.`)
      ) {
        try {
          await api.deleteCourseTimeline(timeline._id)
          this.$toast.success("Kursplanung wurde gelöscht.")
          this.$emit("updateList")
          this.closeDialog()
        } catch (e) {
          this.$toast.error("Löschen fehlgeschlagen.")
        }
      }
    },

    initSaveTimeline(sendPush) {
      this.showPushPopUp = false
      this.saveCourseTimeline(sendPush)
    },

    async saveCourseTimeline(sendPush) {
      if (!this.courseTimelineEdit.course) return this.$toast.error("Bitte wähle ein Kursformat aus")
      if (this.time === "00:00") return this.$toast.error("Bitte gib eine Uhrzeit an")
      if (!this.courseTimelineEdit.repeating && (!this.courseTimelineEdit.visibleAfter || !this.courseTimelineEdit.hideAfter)) {
        return this.$toast.error("Bitte wähle ein Start- und Enddatum aus")
      }

      let formData = JSON.parse(JSON.stringify(this.courseTimelineEdit))
      formData.sendPush = sendPush

      // Konvertiere Datum zurück in Timestamps, falls es sich um einen begrenzten Kurs handelt
      if (!formData.repeating) {
        formData.visibleAfter = this.$moment(formData.visibleAfter).unix() * 1000
        formData.hideAfter = this.$moment(formData.hideAfter).unix() * 1000
      } else {
        formData.visibleAfter = null
        formData.hideAfter = null
      }

      try {
        await api.updateCourseTimeline(formData)
        this.$toast.success("Kursplanung erfolgreich aktualisiert!")
        this.$emit("updateList")
        this.closeDialog()
      } catch (e) {
        this.$toast.error("Fehler beim Speichern der Kursplanung.")
      }
    },

    removeInstructor(id) {
      this.courseTimelineEdit.instructors = this.courseTimelineEdit.instructors.filter((instructorId) => instructorId !== id)
    },

    addInstructor() {
      if (!this.courseTimelineEdit.instructors.includes(null)) {
        this.courseTimelineEdit.instructors.push(null)
      }
    },

    updateInstructor(newId, oldId) {
      if (oldId === null) {
        if (!this.courseTimelineEdit.instructors.includes(newId)) {
          this.courseTimelineEdit.instructors.pop()
          this.courseTimelineEdit.instructors.push(newId)
        } else {
          this.courseTimelineEdit.instructors.pop()
        }
      } else {
        const index = this.courseTimelineEdit.instructors.indexOf(oldId)
        if (index !== -1) {
          this.courseTimelineEdit.instructors.splice(index, 1, newId)
        }
      }
    },

    closeDialog() {
      this.dialog = false
      this.$nextTick(() => {
        this.$router.push("/kurslisten")
      })
    },
  },
  watch: {
    time(val) {
      if (val && this.courseTimelineEdit) {
        this.courseTimelineEdit.hours = parseInt(val.split(":")[0])
        this.courseTimelineEdit.minutes = parseInt(val.split(":")[1])
      }
    },
  },
  computed: {
    selectedDay() {
      let day = this.courseTimelineEdit.day
      let days = [0, 1, 2, 3, 4, 5, 6]
      if (day === 7) day = 0
      days = days.filter((e) => e !== day)
      return days
    },
    visibleAfterText() {
      return this.courseTimelineEdit.visibleAfter ? this.$moment(this.courseTimelineEdit.visibleAfter).format("DD.MM.YYYY") : "Datum wählen"
    },
    hideAfterText() {
      return this.courseTimelineEdit.hideAfter ? this.$moment(this.courseTimelineEdit.hideAfter).format("DD.MM.YYYY") : "Datum wählen"
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .iconAvatar {
  width: 50px !important;
  height: 50px !important;
}
.course-edit-wrapper {
  background-color: #f7f8fa;
  height: 100vh;
  width: 100vw;
  overflow-y: auto;
}

.app-bar-border {
  border-bottom: 1px solid #e0e0e0 !important;
}

.form-container {
  padding-top: 72px;
  padding-bottom: 24px;
  max-width: 700px;
}

.form-card {
  border-radius: 16px;
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
}

.v-btn-toggle {
  width: 100%;
  .v-btn {
    width: 50%;
  }
}

::v-deep .v-chip .v-avatar .lottie-player {
  width: 32px !important;
  height: 32px !important;
}
</style>
